<template>
  <div v-if="tags && tags.length" class="listbox relative" :class="label">
    <Listbox v-slot="{ open }" v-model="model" multiple>
      <ListboxButton
        class="listbox-button relative w-full cursor-default rounded-lg border bg-white py-2 pr-10 pl-3 text-left text-gray-700 shadow-xs focus:outline-2 sm:text-sm"
        :class="{ 'outline-2': open }"
      >
        <span class="block truncate">{{ label }}</span>
        <span
          class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2"
        >
          <ChevronUpDownIcon class="h-5 w-5 text-gray-400" aria-hidden="true" />
        </span>
      </ListboxButton>

      <transition
        leave-active-class="transition duration-100 ease-in"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <ListboxOptions
          class="absolute z-3 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg outline-1 outline-black/5 focus:outline-hidden sm:text-sm"
        >
          <ListboxOption
            v-for="tag in tags"
            v-slot="{ active, selected }"
            :key="tag.key"
            :value="tag.key"
            as="template"
          >
            <li
              :class="[
                active ? `active` : 'text-gray-900',
                'relative cursor-default py-2 pr-4 pl-10 select-none',
              ]"
            >
              <span
                :class="[selected ? 'font-medium' : 'font-normal', 'block']"
                >{{ tag.label }}</span
              >
              <span
                v-if="selected"
                class="check-container absolute inset-y-0 left-0 flex items-center pl-3"
              >
                <CheckIcon class="h-5 w-5" aria-hidden="true" />
              </span>
            </li>
          </ListboxOption>
        </ListboxOptions>
      </transition>
    </Listbox>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import {
  Listbox,
  ListboxButton,
  ListboxOptions,
  ListboxOption,
} from '@headlessui/vue'
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/vue/20/solid'
import { useColorPalette } from '../composables/colorPalette'

const props = defineProps({
  label: { type: String, default: '' },
  tags: { type: Array, default: () => [] },
  highlightColor: { type: String, default: 'default' },
})

const { colorCustomClass } = useColorPalette()

const model = defineModel({
  required: true,
  type: Array,
})

const highlightVariable = computed(() => {
  return `var(--${colorCustomClass(props.highlightColor, null, '')})`
})
</script>

<style scoped>
.listbox-button {
  outline-color: v-bind(highlightVariable);
  outline-offset: -2px;
}

.listbox li.active {
  background-color: color-mix(in oklab, v-bind(highlightVariable), white 95%);
  color: color-mix(in oklab, v-bind(highlightVariable), black 30%);
}

.check-container {
  color: v-bind(highlightVariable);
}
</style>
